/// Handles auth0 Redirect for both single and multi-tenant scenarios.
/// 
/// The basic algorithm is that when this window mounts, it retrieves the portals that
/// the logged in user has access to.  If it returns only 1, as it always will in the 
/// single tenant scenario, it automatically selects that tenant and continues.
///
/// There are settings we need configured via selectTenant (see below) that are required
/// regardless of which scenario we are running so this handles that gracefully.

import * as React from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import '../Main.scss';

import { useAuth0 } from '@auth0/auth0-react';

import {
    Button,
    Col,
    Container,
    Row
} from 'reactstrap';

import { ApplicationState } from '../../Store';
import { actionCreators as RunningImportsActions } from '../../Store/RunningImports';
import { actionCreators as UIStateActions } from '../../Store/UIState';
import {
    Auth0UserSettings,
    GetAuth0Configuration, actionCreators as UserActions
} from '../../Store/User';

import {
    ActiveTenantRequest,
    EmailTenantsAccessStatus,
    PortalTextLogin,
    TenantDescription,
} from '../../Models/Api/strongbox.financialportal';
import { GetActiveConsumerId, IsMultiTenant } from '../../Store/AppSettings';
import {
    GetAvailableTenants,
    GetTenantAccessStatus,
    actionCreators as MultiTenantActions,
} from '../../Store/MultiTenant';
import { GetPortalText, RetrieveLoginText } from '../../Store/PortalAssets';
import { LogException } from '../../Utils/Logging';
import { StringFormat } from '../../Utils/StringUtils';
import { ErrorAction, ErrorBanner, ErrorState } from '../ErrorBanner/ErrorBanner';
import { GetAuth0UserInformation } from '../Utils/Auth0Utils';

import { pathConstants } from '../../Utils/Constants';
import { FetchMethod, fetchWrapper } from '../../Utils/FetchWrapper';

type InjectedReduxState = {
    auth0Config: Auth0UserSettings | undefined;
    loginText?: PortalTextLogin;
    activeConsumerId: string;
    availableTenants?: TenantDescription[];
    tenantAccessStatus?: EmailTenantsAccessStatus;
    isMultiTenant: boolean;
};

type InjectedActionCreators = typeof UIStateActions & typeof MultiTenantActions & typeof RunningImportsActions & typeof UserActions;

type PostLoginProps = {
};

type Props = PostLoginProps & InjectedReduxState & InjectedActionCreators;

const PostLoginComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        auth0Config,
        loginText,
        SetPortalWorking,
        SetPortalIdle,
        GetAvailablePortalsForEmail,
        availableTenants,
        tenantAccessStatus,
        isMultiTenant,
        ValidateRunningImportsConsumer,
        LogUserOut,
    } = props;

    const auth0ContextInterface = useAuth0();
    const { getAccessTokenSilently, logout, isAuthenticated, isLoading } = useAuth0();

    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);
    const [navigateUrl, setNavigateUrl] = React.useState<string | undefined>(undefined);

    const location = useLocation();

    const [searchString, setSearchString] = React.useState<string>('');

    const defaultAction: ErrorAction = React.useMemo(() => {
        return {
            id: 'ok',
            text: loginText?.okay || '',
            onAction: (_: string) => {
                LogUserOut(logout);
                setErrorState(undefined);
            },
        }

        // nothing needed in dependencies beyond commonText, generalMessages and loginText
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [loginText]);

    const formattedText = React.useMemo(() => {
        return {
            loadError: StringFormat(loginText?.templateUnableToGetListOfAvailablePortals || '{0}', loginText?.appTitle || ''),
            header: StringFormat(loginText?.templateSelectTenantHeader || '{0}', loginText?.appTitle || ''),
            noTenants: StringFormat(loginText?.templateSelectTenantNoAvailableTenants || '{0}', loginText?.appTitle || ''),
            noAccessToUrl: StringFormat(loginText?.templateUserDoesNotHaveAccessToURL || '{0}', loginText?.appTitle || ''),
        }
    }, [loginText]);

    const loadUserInformation = async (): Promise<void> => {
        if (!auth0Config) {
            return;
        }

        const workingState = "post-login-load-user-information";
        SetPortalWorking(workingState);

        try {
            const userInformation = await GetAuth0UserInformation(auth0ContextInterface, auth0Config);

            // At this point we have userInformation.idpUserProperties.email
            if (!(userInformation.idpUserProperties && userInformation.idpUserProperties.email && userInformation.idpUserProperties.user_id)) {
                console.error('PostLogin:loadUserInformation, idpUserProperties, idpUserProperties.email or idpUserProperties.user_id is not available');
                setErrorState({
                    summaryMessage: formattedText.loadError,
                    extraInformation: undefined,
                    actions: [defaultAction],
                });
                return;
            }

            await GetAvailablePortalsForEmail(userInformation.idpUserProperties.email, userInformation.idpUserProperties.user_id);

        } catch (exception) {
            LogException(
                `Failure getting user information for auth0`,
                exception
            );

            console.error('caught error in loadUserInformation');
            console.error(exception);
            setErrorState({
                summaryMessage: StringFormat(loginText?.templateGeneralFatalError || '{0}', loginText?.stdErrorAction || ''),
                extraInformation: undefined,
                actions: [defaultAction],
            });
        } finally {
            SetPortalIdle(workingState);
        }
    }

    const additionalHeaders = React.useMemo(() => {
        return loginText?.selectTenantAdditionalHeaders || [];
    }, [loginText])

    React.useEffect(() => {
        let searchString = location.search;

        if (!!searchString) {
            const sp = new URLSearchParams(location.search);
            let newParams = '';

            // on the forEach call, index will be the actual name of the search
            // parameter, param is the actual value.
            sp.forEach((param, index) => {
                // code and state come from auth0 and we don't actually want to pass them on.  If we find them we should
                // also call handleRedirectCallback

                if ((index !== 'code') && (index !== 'state')) {
                    if (!!newParams) {
                        newParams += '&'
                    }
                    newParams += `${index}=${param}`;
                }
            });

            if (!!newParams) {
                searchString = `?${newParams}`
            } else {
                searchString = '';
            }
        }

        setSearchString(searchString);
        RetrieveLoginText();

        // No dependencies, I want this to run on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    React.useEffect(() => {
        if (!(loginText && auth0Config) || isLoading || !isAuthenticated) {
            return;
        }

        loadUserInformation();
        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [auth0Config, formattedText, isAuthenticated, isLoading]);

    const selectTenant = async (tenant: TenantDescription): Promise<void> => {
        if (!auth0Config) {
            console.error('PostLogin:selectTenant, auth0Config is undefined');
            return;
        }

        const workingMessage = 'post-login-select-tenant';

        SetPortalWorking(workingMessage);

        try {
            const token = await getAccessTokenSilently(
                {
                    audience: auth0Config.audience,
                    scope: auth0Config.scope,
                }
            );

            const request: ActiveTenantRequest = {
                consumerId: tenant.consumerId,
                nameId: tenant.nameId,
            }

            // We have to pass in the bearer token since within fetchWrapper it uses the value from
            // the redux store to get the bearer token if you pass true for requireAuth.

            await fetchWrapper('/api/tenant/activetenant',
                {
                    method: FetchMethod.Post,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(request)
                },
                true,
                false
            );

            // In a multi-tenant environment the same browser can be used to log into different
            // tenants. The running imports list lives in session storage and would otherwise bleed
            // from one tenant to the next, so validate it against the tenant being selected and
            // clear it if it belongs to a different (or no) consumer.
            if (isMultiTenant) {
                ValidateRunningImportsConsumer(tenant.consumerId);
            }

            const destinationUrl = `${pathConstants.home}${searchString}`;

            setNavigateUrl(destinationUrl);
        }
        catch (exception) {
            console.error("Failed setting active tenant");
            LogException("Failed setting active tenant", exception);

            setErrorState({
                summaryMessage: loginText?.selectTenantFailedSelectingTenant || '',
                extraInformation: undefined,
                actions: [defaultAction],
            });
        }
        finally {
            SetPortalIdle(workingMessage);
        }
    }

    React.useEffect(() => {
        if (!availableTenants) {
            return;
        }

        if (availableTenants.length === 1) {
            selectTenant(availableTenants[0]);
        } else if (availableTenants.length <= 0) {
            // No portals available to select. The server distinguishes two reasons via tenantAccessStatus:
            //   - NoAccessToRequestedUrl: the user has access to one or more portals but NOT the specific
            //     site (URL) they navigated to. This is the case the multi-tenant access fix addresses.
            //   - otherwise (NoAvailablePortals): the user's email domain has no portals at all, which is
            //     most likely a configuration error.
            // Show the message that matches the reason instead of silently logging them into a tenant they
            // happen to belong to.
            const summaryMessage = tenantAccessStatus === 'NoAccessToRequestedUrl'
                ? formattedText.noAccessToUrl
                : formattedText.noTenants;

            setErrorState({
                summaryMessage,
                extraInformation: undefined,
                actions: [defaultAction],
            });
        }

        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableTenants])

    const tenantList = React.useMemo(() => {
        if (availableTenants === undefined) {
            return (<React.Fragment></React.Fragment>);
        }

        // If there's only one possible destination, auto nav to it which is handled in the useEffect above
        if (availableTenants.length === 1) {
            return (<React.Fragment></React.Fragment>);
        }

        if (availableTenants.length <= 0) {
            // The user has no portal available on this site; the error banner is raised from the
            // useEffect above. Render nothing here.
            return (<React.Fragment></React.Fragment>);
        }

        return (
            <div className={'login-team-selection-container'}>
                {
                    availableTenants.map((tenant, index) => {
                        return (
                            <Button
                                className={'extrasmall'}
                                key={`select-tenant-selection-button-${index}`}
                                onClick={() => {
                                    selectTenant(tenant);
                                }}
                            >
                                {tenant.displayName}
                            </Button>
                        );
                    })
                }
            </div>
        );

        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [availableTenants]);

    if (!!navigateUrl) {
        return (<Navigate to={navigateUrl} />);
    }

    if (!errorState && (!availableTenants || availableTenants.length <= 1)) {
        return (<React.Fragment />);
    }

    return (
        <div
            className={`full-screen-region lender-region content-region control-region control-region-lender login-region`}
        >
            <Container className={'post-login-container strongbox-basic-content-container'}>

                {!!errorState && (
                    <React.Fragment>
                        <Row>
                            <Col>
                                <ErrorBanner
                                    errorState={errorState}
                                    onDefaultActionButton={() => {
                                        setErrorState(undefined);
                                    }}
                                    defaultActionText={loginText?.okay || ''}
                                />
                            </Col>
                        </Row>
                    </React.Fragment>
                )}
                {!errorState && (!!availableTenants) && (availableTenants.length > 1) && (
                    <React.Fragment>
                        <Row>
                            <Col className={'col-centered'}>
                                <h1>{formattedText.header}</h1>
                            </Col>
                        </Row>
                        <Row>
                            <Col className={'col-centered'}>
                                {
                                    additionalHeaders.map((h, index) => {
                                        return (
                                            <h3
                                                key={`select-tenant-additional-header-${index}`}
                                            >
                                                {h}
                                            </h3>
                                        );
                                    })
                                }
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {tenantList}
                            </Col>
                        </Row>
                    </React.Fragment>
                )}
                {!errorState && !!availableTenants && (availableTenants.length <= 1) && (<React.Fragment />)}
            </Container>
        </div>
    );
}

export const PostLogin = connect<InjectedReduxState, InjectedActionCreators, PostLoginProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            auth0Config: GetAuth0Configuration(appState),
            loginText: GetPortalText<PortalTextLogin>(appState, 'loginPage'),
            activeConsumerId: GetActiveConsumerId(appState),
            availableTenants: GetAvailableTenants(appState),
            tenantAccessStatus: GetTenantAccessStatus(appState),
            isMultiTenant: IsMultiTenant(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...UIStateActions,
            ...MultiTenantActions,
            ...RunningImportsActions,
            ...UserActions,
        },
        dispatch
    )
)(PostLoginComponent);
